import '@polymer/polymer/polymer-legacy.js';
import { GetConnectionLookup } from '../../modules/GetConnectionLookup.js';
import { PickAnAttribute } from '../../modules/PickAnAttribute.js';
import { Polymer } from '@polymer/polymer/lib/legacy/polymer-fn.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';
import { Round } from '../../modules/Round.js';
import { KatapultGeometry } from 'katapult-toolbox';
import '../katapult-elements/katapult-button.js';
import { KatapultJob } from '../../modules/katapult-job/KatapultJob.js';
import { SnapLocationToConnectionPath } from '../../modules/SnapLocationToConnectionPath.js';
import '../katapult-elements/katapult-dialog.js';

Polymer({
  _template: html`
    <style>
      #gpsActions {
        position: absolute;
        bottom: 70px;
        width: calc(100% - 16px);
        margin-left: 8px;
        display: flex;
        opacity: 0;
        pointer-events: none;
        z-index: 2;
        transition: all 0.3s;
      }
      #gpsActions[opened=''] {
        bottom: 80px;
        opacity: 1;
        pointer-events: auto;
      }
      #gpsActions katapult-button {
        flex-grow: 1;
        background: white;
        margin: 4px;
        @apply --shadow-elevation-2dp;
      }
    </style>

    <div id="gpsActions" opened$="[[showGPSActions]]">
      <katapult-button
        icon="[[getIconFromGPSState('snapshot', positionIsBasedOnGPS, gpsPositionIsAggregate, centerMapOnGPS, mapPanned)]]"
        on-click="tryToUpdateToGPSPosition"
        disabled="[[gpsAggregateRunning]]"
        >Use GPS Position</katapult-button
      >
      <katapult-button
        icon="[[getIconFromGPSState('aggregate', positionIsBasedOnGPS, gpsPositionIsAggregate, centerMapOnGPS, mapPanned)]]"
        on-click="toggleGPSAggregate"
        >{{gpsAggregateButtonText}}</katapult-button
      >
    </div>

    <katapult-dialog id="gpsWarning" title="GPS Warning">
      <template is="dom-if" if="[[gpsData._connected]]">
        <p>Are you sure you want to place this item using your GPS position?</p>
        <!-- Accuracy Warning -->
        <template is="dom-if" if="[[!gpsData._accurate]]">
          <p>Your GPS is accurate to within [[gpsAccuracy]].</p>
        </template>
        <!-- Delta Warning -->
        <template is="dom-if" if="[[showGPSDeltaWarning]]">
          <p>The delta between the current position and your GPS position is [[gpsDelta]].</p>
        </template>
      </template>
      <template is="dom-if" if="[[!gpsData._connected]]">
        <p>We're having trouble connecting to your GPS</p>
        <p>Check that you have enabled GPS access for this app and then try again.</p>
      </template>
      <div slot="buttons" style="display: flex; justify-content: space-between; width: 100%;">
        <katapult-button color="var(--paper-red-500)" dialog-dismiss>Cancel</katapult-button>
        <template is="dom-if" if="[[gpsData._connected]]">
          <katapult-button on-click="updateToGPSPosition">Use GPS Anyway</katapult-button>
        </template>
      </div>
    </katapult-dialog>
  `,

  /*
    fires toast, prompt, cancel-prompt, secondary-prompt, cancel-secondary-prompt, tertiary-prompt, cancel-tertiary-prompt, zoom-to-active
  */
  is: 'move-item',

  properties: {
    activeTool: {
      type: String
    },
    activeCommand: {
      type: String,
      notify: true
    },
    activeItem: {
      type: String,
      notify: true
    },
    color: {
      type: String,
      value: '#999'
    },
    icon: {
      type: String,
      value: 'arrow-forward'
    },
    connsToLabel: {
      type: Array,
      notify: true
    },
    showAnglesOfNode: {
      type: String,
      notify: true
    },
    pointerAsCrosshair: {
      type: Boolean,
      value: false,
      notify: true
    },
    gpsAggregateButtonText: {
      type: String,
      value: 'Take Aggregate'
    },
    recordNodeMoveAttribute: String,
    jobid: String,
    jobStyles: Object,
    latitude: {
      type: Number,
      notify: true
    },
    longitude: {
      type: Number,
      notify: true
    },
    modelDefaults: {
      type: Object,
      value: () => ({})
    },
    centerMapOnGPS: {
      type: Boolean
    },
    mapPanned: {
      type: Boolean
    },
    gpsCircleConfig: {
      type: Object,
      value: () => ({}),
      notify: true
    }
  },

  observers: [
    'setPositionIsBasedOnGPS(centerMapOnGPS, mapPanned)',
    'updateGpsCircleConfig(gpsData.*, showGPSActions, aggregatedGPSData)',
    'updateGpsCircle(gpsCircleConfig)'
  ],

  detached: function () {
    clearInterval(this.gpsAggregateIntervalUpdate);
  },

  init: function (button) {
    this.resetGPSActions();
    this.set('connsToLabel', []);
    this.set('showAnglesOfNode', '');
    this.activeCommand = 'select';
    this.fire('prompt', {
      text: 'Select Item To Move'
    });
    this.fire('cancel-secondary-prompt');
    this.fire('cancel-tertiary-prompt');
  },

  map_pan: function (latitude, longitude, closest) {
    // If we're not actively aggregating GPS data
    if (!this.gpsAggregateRunning) {
      // Mark that the move was not based on GPS since it was manual
      this.positionIsBasedOnGPS = false;
      this.gpsPositionIsAggregate = false;

      // Clear any saved GPS data
      this.snapshotGPSData = null;
      this.aggregatedGPSData = null;
    }

    if (this.activeCommand == 'select') {
      if (closest == null) {
        this.activeItem = '';
      } else if (closest.item[0] != 'c') {
        this.activeItem = closest.item;
      } else {
        this.activeItem = '';
      }
    } else if (this.activeCommand == 'confirmMove') {
      this.updateLocalActiveItemLocation(this.latitude, this.longitude);
    }
  },

  checkIfCanMove: function (nodeData) {
    // Check if the node is not a primary pole and if so, don't allow the user to move it
    if (nodeData?.reference_data?.primary_pole === false) {
      const otherJobId = nodeData?.reference_data?.job_id;
      try {
        FirebaseWorker.ref(`photoheight/jobs/${otherJobId}/name`)
          .once('value')
          .then((s) => {
            const otherJobName = s.val() || 'another job';
            this.fire('toast', {
              text: `This is a reference to ${otherJobName} and cannot be moved. You must move the pole in that job instead.`,
              duration: 6000
            });
          });
      } catch (e) {
        this.fire('toast', {
          text: `This is a reference to another job and cannot be moved.`,
          duration: 6000
        });
      }
      return false;
    }
    return true;
  },

  select: function (closest, latitude, longitude, detail) {
    // Don't let the user start a new move on an item still being saved.
    if (this.pendingMoveItems?.has(this.activeItem)) {
      this.fire('toast', 'This item is still saving its last move — please wait a moment and try again.');
      return;
    }

    let item = null;
    if (typeof detail?.callback === 'function') {
      this.confirmMoveCallback = detail.callback;
    }
    if (this.activeItem[0] == 'n') {
      var id = this.activeItem.slice(1);
      item = this.getByKey('nodes', id);
      if (!this.checkIfCanMove(item)) {
        return;
      }
      const marker = window.loadRenderMap.pointLocations[id];
      if (marker) {
        if (marker.data) {
          for (var connId in marker.data.n) {
            var line = window.loadRenderMap.lineLocations[connId];
            if (line) {
              line.setOptions({ strokeOpacity: 0.2 });
              line.override = { strokeOpacity: true };
            }
          }
        }
      }
      const connLookup = window.loadRenderMap.pointLocations[id]?.data?.n;
      for (var connId in connLookup) {
        this.push('connsToLabel', connId);
      }
      this.set('showAnglesOfNode', id);
    } else if (this.activeItem[0] == 's') {
      var ids = this.activeItem.slice(1).split(':');
      item = this.getByKey('connections', ids[0]).sections[ids[1]];
    }
    if (item != null) {
      this.pointerAsCrosshair = true;
      this.previousLatitude = item.latitude;
      this.previousLongitude = item.longitude;
      this.fire('zoom-to-active');
      this.activeCommand = 'confirmMove';
      this.fire('prompt', { text: 'Confirm Move' });
      this.fire('secondary-prompt', { text: 'Cancel' });
      this.fire('tertiary-prompt', { text: 'GPS...' });
    } else {
      this.fire('toast', 'Please select closer to an existing item.');
    }
  },

  updateLocalActiveItemLocation: function (latitude, longitude) {
    const itemId = this.activeItem.slice(1);
    const itemType = this.activeItem[0];

    switch (itemType) {
      case 'n': {
        const node = this.getByKey('nodes', itemId);
        if (!this.checkIfCanMove(node)) return;

        const oldLatLngForNode = new google.maps.LatLng(this.previousLatitude, this.previousLongitude);
        const newLatLngForNode = new google.maps.LatLng(latitude, longitude);
        const marker = globalThis.loadRenderMap.pointLocations[itemId];
        if (!marker) return;

        // Move the node locally
        marker.position = newLatLngForNode;
        globalThis.loadRenderMap.updatePointLabel(itemId);

        // Move the connections locally
        for (const connId in marker.data?.n) {
          const idNumber = marker.data.n[connId];
          const line = globalThis.loadRenderMap.lineLocations[connId];
          const path = line.getPath();
          let index = 0;
          if (idNumber != line.idNumber) index = path.getLength() - 1;
          if (line.idNumber == 2) index = index == 0 ? path.getLength() - 1 : 0;
          path.dontSaveChange = true;
          path.setAt(index, newLatLngForNode);
          const length = this.getConnPolylineLength(path);
          line.length = length;
          line.data.d = length;

          // Get the full path of the connection including any breakpoints
          const connectionPathPoints = path.getArray().map((vertex) => [vertex.lat(), vertex.lng()]);

          // Loop the section list and update positions accordingly
          if (globalThis.loadRenderMap.sectionList[connId]) {
            for (const sectionId in globalThis.loadRenderMap.sectionList[connId].sections) {
              // Get the section marker and its lat/lng
              const sectionMarker = globalThis.loadRenderMap.pointLocations[`${connId}:${sectionId}`];
              const { lat: sectionLat, lng: sectionLng } = sectionMarker.position.toJSON();

              // Get the new position for the section marker
              const newPosition = SnapLocationToConnectionPath(sectionLat, sectionLng, connectionPathPoints);

              // Update the section marker position on the map
              sectionMarker.position = new google.maps.LatLng(newPosition.latitude, newPosition.longitude);
            }
          }

          globalThis.loadRenderMap.updateConnLabel(connId);
        }

        // Get connections for the node
        const getConnectionLookup = GetConnectionLookup(this.domHost.nodes, this.domHost.connections);
        const nodeConnections = getConnectionLookup[itemId] ?? [];

        for (let i = 0; i < nodeConnections.length; i++) {
          const connId = nodeConnections[i].connId;
          var conn = nodeConnections[i].connData;
          var toNodeId = nodeConnections[i].toNodeId;
          if (conn != null) {
            // Get a reference to the other node on the connection
            const otherNode = this.domHost.nodes[toNodeId];
            // Create a lat/long object for the other node's position
            const otherNodeType = PickAnAttribute(otherNode.attributes, this.modelDefaults.node_type_attribute);
            // Find any connections of type "down guy" or "pushbrace" because we
            // want to lock the angle and distance of other nodes on those connections
            const connectionType = PickAnAttribute(conn.attributes, this.modelDefaults.connection_type_attribute);
            if (
              (connectionType == 'down guy' || connectionType == 'pushbrace') &&
              (otherNodeType == 'existing anchor' || otherNodeType == 'new anchor' || otherNodeType == 'pushbrace')
            ) {
              // Get the existing anchor location
              const oldAnchorLatLng = new google.maps.LatLng(otherNode.latitude, otherNode.longitude);
              // Move the anchor node and connection
              const offsetDist = google.maps.geometry.spherical.computeDistanceBetween(oldAnchorLatLng, oldLatLngForNode);
              const offsetDir = google.maps.geometry.spherical.computeHeading(oldLatLngForNode, oldAnchorLatLng);

              // Update the anchor position
              const newAnchorLatLng = google.maps.geometry.spherical.computeOffset(newLatLngForNode, offsetDist, offsetDir);
              const anchorMarker = globalThis.loadRenderMap.pointLocations[toNodeId];
              if (anchorMarker) {
                anchorMarker.position = newAnchorLatLng;
                if (anchorMarker.label) anchorMarker.label.position = newAnchorLatLng;
              }

              // Update the connection position
              const idNumber = marker.data.n[connId];
              const line = globalThis.loadRenderMap.lineLocations[connId];
              const path = line.getPath();
              let index = path.getLength() - 1;
              if (idNumber != line.idNumber) index = 0;
              if (line.idNumber == 2) index = index == 0 ? path.getLength() - 1 : 0;
              path.dontSaveChange = true;
              path.setAt(index, newAnchorLatLng);
              const length = this.getConnPolylineLength(path);
              line.length = length;
              line.data.d = length;
              globalThis.loadRenderMap.updateConnLabel(connId);
            }
          }
        }
        break;
      }
      case 's': {
        const [connectionId] = itemId.split(':');
        const connection = this.getByKey('connections', connectionId);
        const n1 = this.getByKey('nodes', connection.node_id_1);
        const n2 = this.getByKey('nodes', connection.node_id_2);

        const connectionPathPoints = [[n1.latitude, n1.longitude], ...(connection.breakpoints ?? []), [n2.latitude, n2.longitude]];
        const pointOnLine = SnapLocationToConnectionPath(latitude, longitude, connectionPathPoints);
        const marker = globalThis.loadRenderMap.pointLocations[itemId];
        if (marker) marker.position = new google.maps.LatLng(pointOnLine.latitude, pointOnLine.longitude);
        break;
      }
    }
  },

  getConnPolylineLength: function (path, points) {
    if (!path) {
      for (var i = 0; i < points.length; i++) {
        points[i] = new google.maps.LatLng(points[i][0], points[i][1]);
      }
      path = new google.maps.MVCArray(points);
    }
    return Round(google.maps.geometry.spherical.computeLength(path) / 0.3048, 1);
  },

  getGPSLocation: function () {
    if (!this.positionIsBasedOnGPS) return null;

    // TODO (2024-11-18): This is a temporary fix to stop aggregates when this function is called from another tool
    const gpsPositionIsAggregateOrAggregateRunning = this.gpsPositionIsAggregate || this.gpsAggregateRunning;
    // If the aggregate is running, stop it
    if (this.gpsAggregateRunning) this.stopGPSAggregate();

    // Get the GPS data to use based on the current state
    const gpsDataSourceToUse =
      // If GPS position is an aggregate, use the aggregate data
      gpsPositionIsAggregateOrAggregateRunning
        ? this.aggregatedGPSData
        : // If the map is centered on GPS, use the live GPS data; otherwise, use the snapshot GPS data
          this.centerMapOnGPS && !this.mapPanned
          ? this.gpsData
          : this.snapshotGPSData;

    return {
      latitude: gpsDataSourceToUse.latitude,
      longitude: gpsDataSourceToUse.longitude,
      accuracy: gpsDataSourceToUse.accuracy,
      elevation: gpsDataSourceToUse.altitude,
      elevation_accuracy: gpsDataSourceToUse.altitudeAccuracy
    };
  },

  confirmMove: async function () {
    const activeItem = this.activeItem;
    const itemType = activeItem[0];
    const itemId = activeItem.slice(1);

    // Guard against re-confirming an item that's still saving its previous move.
    if (this.pendingMoveItems?.has(activeItem)) return;

    // Resolve the final coordinates now, before closing the tool.
    const gpsLocation = this.getGPSLocation();
    const latitude = gpsLocation?.latitude || this.latitude;
    const longitude = gpsLocation?.longitude || this.longitude;

    let node = null;
    if (itemType == 'n') {
      node = this.getByKey('nodes', itemId);
      if (!this.checkIfCanMove(node)) return;

      // Restore the connections' normal opacity - purely local, no reason to wait on anything
      const marker = window.loadRenderMap.pointLocations[itemId];
      if (marker?.data) {
        for (var connId in marker.data.n) {
          var line = window.loadRenderMap.lineLocations[connId];
          if (line) {
            line.setOptions({ strokeOpacity: line.data.o });
            line.override = null;
          }
        }
      }
    }

    // Close the tool immediately. The actual save continues below, in the background.
    this.fire('zoom-to-active');
    this.fire('update-active-item-location');
    this.pointerAsCrosshair = false;
    if (this.confirmMoveCallback) {
      const positionIsBasedOnGPS = Boolean(this.positionIsBasedOnGPS);
      this.confirmMoveCallback({ positionIsBasedOnGPS });
      this.confirmMoveCallback = null;
    }
    this.init();

    // Persist the move. The tool has already closed, so report progress/failure via toast instead of blocking on any of this.
    await this.saveMove(activeItem, itemType, itemId, latitude, longitude, node);
  },

  saveMove: async function (activeItem, itemType, itemId, latitude, longitude, node) {
    (this.pendingMoveItems ??= new Set()).add(activeItem);
    try {
      const job = KatapultJob.fromId(this.jobid);
      if (itemType == 'n') {
        await job.setNodeLocation(itemId, [latitude, longitude]);
        if (this.recordNodeMoveAttribute) await job.updateNodeAttribute(itemId, this.recordNodeMoveAttribute, { value: true });
        // If the pole has reference data and is the primary pole, we need to adjust
        // the position for the reference pole as well in the other job
        if (node?.reference_data?.primary_pole === true && node?.reference_data?.job_id != null) {
          const referenceJob = KatapultJob.fromId(node.reference_data.job_id);
          await referenceJob.setNodeLocation(itemId, [latitude, longitude]);
        }
      } else if (itemType == 's') {
        const [connectionId, sectionKey] = itemId.split(':');
        await job.setSectionLocation(connectionId, sectionKey, [latitude, longitude]);
      }
    } catch (error) {
      console.error(error);
      this.fire('toast', { text: error });
    } finally {
      this.pendingMoveItems.delete(activeItem);
    }
  },

  action_secondary: function () {
    this.set('connsToLabel', []);
    this.set('showAnglesOfNode', '');

    this.updateLocalActiveItemLocation(this.previousLatitude, this.previousLongitude);

    this.fire('zoom-to-active');
    setTimeout(() => this.fire('update-active-item-location'));
    this.pointerAsCrosshair = false;
    this.init();

    if (this.activeItem[0] == 'n') {
      var id = this.activeItem.slice(1);
      const marker = window.loadRenderMap.pointLocations[id];
      if (marker) {
        if (marker.data) {
          for (var connId in marker.data.n) {
            var line = window.loadRenderMap.lineLocations[connId];
            if (line) {
              line.setOptions({ strokeOpacity: line.data.o });
              line.override = null;
            }
          }
        }
      }
    }

    // TODO (2024-11-14): Should we even do this callback?
    if (this.confirmMoveCallback) {
      // Don't assume position was set with GPS since this is a cancel
      this.confirmMoveCallback({ positionIsBasedOnGPS: false });
      this.confirmMoveCallback = null;
    }
  },

  toggleGPSAggregate: function () {
    if (!this.gpsData._connected) {
      this.$.gpsWarning.open();
      return;
    }

    this.gpsAggregateRunning ? this.stopGPSAggregate() : this.startGPSAggregate();
  },

  startGPSAggregate: function () {
    if (this.activeItem[0] == 'n') {
      const nodeId = this.activeItem.slice(1);
      const node = this.getByKey('nodes', nodeId);
      if (!this.checkIfCanMove(node)) return;
    }

    let secondsLeft = 30;
    const locationSamples = [];
    const gpsAggregatorCallback = () => {
      if (--secondsLeft <= 0 || !this.gpsAggregateRunning) {
        this.stopGPSAggregate();
        return;
      }
      this.gpsAggregateButtonText = `(${secondsLeft}) Tap to Stop`;

      locationSamples.push({
        latitude: this.gpsData.latitude,
        longitude: this.gpsData.longitude,
        accuracy: this.gpsData.accuracy,
        altitude: this.gpsData.altitude,
        altitudeAccuracy: this.gpsData.altitudeAccuracy
      });
      const totalSamples = locationSamples.length;

      const averageLatitude = locationSamples.reduce((total, { latitude }) => total + latitude, 0) / totalSamples;
      const averageLongitude = locationSamples.reduce((total, { longitude }) => total + longitude, 0) / totalSamples;
      const averageAccuracy = locationSamples.reduce((total, { accuracy }) => total + accuracy, 0) / totalSamples;
      const averageAltitude =
        this.gpsData.altitude != null ? locationSamples.reduce((total, { altitude }) => total + altitude, 0) / totalSamples : null;
      const averageAltitudeAccuracy =
        this.gpsData.altitudeAccuracy != null
          ? locationSamples.reduce((total, { altitudeAccuracy }) => total + altitudeAccuracy, 0) / totalSamples
          : null;

      // Update the dropper position
      this.latitude = averageLatitude;
      this.longitude = averageLongitude;

      this.aggregatedGPSData = {
        latitude: averageLatitude,
        longitude: averageLongitude,
        accuracy: averageAccuracy,
        altitude: averageAltitude,
        altitudeAccuracy: averageAltitudeAccuracy
      };
      // Set flags to indicate that the position is based on GPS and that it is an aggregate
      this.positionIsBasedOnGPS = true;
      this.gpsPositionIsAggregate = true;
      this.snapshotGPSData = null;

      this.runToolSpecificLocalLocationUpdateCallbacks(averageLatitude, averageLongitude);
    };

    this.gpsAggregateRunning = true;
    // Call the aggregator callback immediately, and then...
    gpsAggregatorCallback();
    // Call the aggregator callback every second
    this.gpsAggregateIntervalUpdate = setInterval(gpsAggregatorCallback, 1000);
  },

  stopGPSAggregate: function () {
    clearInterval(this.gpsAggregateIntervalUpdate);
    this.gpsAggregateRunning = false;
    this.gpsAggregateButtonText = 'Take Aggregate';
  },

  action_tertiary: function () {
    this.toggleGPSActions();
  },

  toggleGPSActions: function () {
    // Don't toggle if an aggregate is running
    if (!this.gpsAggregateRunning) this.showGPSActions = !this.showGPSActions;
  },

  tryToUpdateToGPSPosition: function () {
    this.showGPSDeltaWarning = false;
    if (this.gpsData._connected) {
      const deltaThreshold = this.useMetricUnits ? 30 : 100 * 0.3048; // 30 meters (or 100 feet)
      const actualDelta = KatapultGeometry.CalcDistance(this.gpsData.latitude, this.gpsData.longitude, this.latitude, this.longitude);
      this.showGPSDeltaWarning = actualDelta > deltaThreshold;

      const feetOrMeters = this.useMetricUnits ? 'meters' : 'feet';
      this.gpsDelta = `${Round(actualDelta / (this.useMetricUnits ? 1 : 0.3048), 0)} ${feetOrMeters}`;
      this.gpsAccuracy = `${Round(this.gpsData.accuracy / (this.useMetricUnits ? 1 : 0.3048), 0)} ${feetOrMeters}`;
    }

    const shouldShowWarning = this.showGPSDeltaWarning || !this.gpsData._accurate || !this.gpsData._connected;
    shouldShowWarning ? this.$.gpsWarning.open() : this.updateToGPSPosition();
  },

  resetGPSActions: function () {
    this.showGPSActions = false;
    this.stopGPSAggregate();

    // Reset flags
    this.positionIsBasedOnGPS = false;
    this.gpsPositionIsAggregate = false;

    // Clear any saved GPS data
    this.snapshotGPSData = null;
    this.aggregatedGPSData = null;
  },

  updateToGPSPosition: function () {
    if (this.activeItem[0] == 'n') {
      const node = this.getByKey('nodes', this.activeItem.slice(1));
      if (!this.checkIfCanMove(node)) return;
    }

    // Close the GPS warning dialog
    if (this.$.gpsWarning.opened) this.$.gpsWarning.close();

    // Don't update position if latitude or longitude is null
    if (this.gpsData.latitude == null || this.gpsData.longitude == null) return;

    // Save a snapshot of the current GPS data
    this.snapshotGPSData = {
      latitude: this.gpsData.latitude,
      longitude: this.gpsData.longitude,
      accuracy: this.gpsData.accuracy,
      altitude: this.gpsData.altitude,
      altitudeAccuracy: this.gpsData.altitudeAccuracy
    };

    // Position is based on GPS will be true until the user pans the map
    this.positionIsBasedOnGPS = true;
    this.gpsPositionIsAggregate = false;
    this.aggregatedGPSData = null;

    // Update position on the map
    this.latitude = this.snapshotGPSData.latitude;
    this.longitude = this.snapshotGPSData.longitude;

    this.runToolSpecificLocalLocationUpdateCallbacks(this.latitude, this.longitude);
  },

  runToolSpecificLocalLocationUpdateCallbacks: function (latitude, longitude) {
    // If GPS actions are being used by drawOnMap, tell that tool its position has been updated
    if (this.activeTool == 'drawOnMap') this.domHost.$.drawOnMap.positionUpdated = true;
    // Only update the active item if we aren't using a different tool
    if (this.activeTool == 'moveItem') this.updateLocalActiveItemLocation(latitude, longitude);
  },

  setPositionIsBasedOnGPS: function (centerMapOnGPS, mapPanned) {
    // This is one place that sets this flag; there are others
    this.positionIsBasedOnGPS = centerMapOnGPS && !mapPanned;
  },

  updateGpsCircleConfig: function () {
    const { gpsData, showGPSActions, aggregatedGPSData } = this;

    const shouldShowGpsAggregateCircle = aggregatedGPSData != null;
    const shouldShowGpsAccuracyCircle = showGPSActions === true;
    const shouldShowCircle = shouldShowGpsAccuracyCircle || shouldShowGpsAggregateCircle;

    const gpsCircleConfig = { visible: false };
    if (shouldShowCircle) {
      Object.assign(gpsCircleConfig, { visible: true });
      if (shouldShowGpsAggregateCircle) {
        const { latitude, longitude, accuracy } = aggregatedGPSData;
        Object.assign(gpsCircleConfig, { latitude, longitude, accuracy });
      } else if (shouldShowGpsAccuracyCircle) {
        const { latitude, longitude, accuracy } = gpsData;
        Object.assign(gpsCircleConfig, { latitude, longitude, accuracy });
      }
    }

    Object.assign(this, { gpsCircleConfig });
  },

  updateGpsCircle: function () {
    if (!globalThis?.google?.maps?.Circle) return;

    const { gpsCircleConfig } = this;
    const { visible, latitude, longitude, accuracy } = gpsCircleConfig ?? {};

    const map = this.googlemap?.map ?? null;

    const circle = (this.gpsCircle ??= new globalThis.google.maps.Circle({
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillOpacity: 0.35
    }));

    circle.setMap(visible ? map : null);

    if (!visible) return;

    const isInaccurate = accuracy > 50;
    const color = isInaccurate ? '#F44336' : '#FCEF5F';
    circle.setOptions({
      strokeColor: color,
      fillColor: color,
      center: { lat: latitude, lng: longitude },
      radius: accuracy
    });
  },

  getByKey: function (type, id) {
    return this.domHost.$[type].child(id);
  },

  getIconFromGPSState(button, positionIsBasedOnGPS, gpsPositionIsAggregate) {
    const isSnapshotAndNotUsingAggregate = button == 'snapshot' && !gpsPositionIsAggregate;
    const isAggregateAndUsingAggregate = button == 'aggregate' && gpsPositionIsAggregate == true;
    const showIcon = positionIsBasedOnGPS && (isSnapshotAndNotUsingAggregate || isAggregateAndUsingAggregate);

    return showIcon ? 'gps_fixed' : '';
  }
});
